/**
 * Basket module — qty steppers, add-to-basket, header badge, toast,
 * and the /purchase/basket page (fetch + render + remove).
 *
 * All basket operations go through our own /api/basket/* endpoints
 * (backed by BasketHold rows). The client never talks to a third
 * party — local dev hits the same backend production does.
 */

// Trash-can icon for the per-line Remove buttons (stroke style matches the
// basket's other inline SVGs). Buttons carry title/aria-label="Remove".
const TRASH_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>'

// CSRF token, sent as X-Security-Token on every mutating /api/basket/* call.
// Anonymous (cacheable) pages render an EMPTY <meta name="security-id"> — they
// deliberately don't start a session (a Set-Cookie would defeat the public
// cache). So on those pages we fetch a token lazily from /csrf-token on first
// interaction and cache it for the rest of the page session. Logged-in / basket
// pages already carry a session and render the token inline (no fetch needed).
let _csrfCache = null
function csrfToken () {
  if (_csrfCache) return _csrfCache
  const el = document.querySelector('meta[name="security-id"]')
  return el && el.content ? el.content : ''
}
// Prime _csrfCache before firing a mutating request. Callers MUST await this at
// the start of any user-triggered add/remove flow so the synchronous csrfToken()
// reads below return a real token even on a fresh, sessionless page view.
async function ensureToken () {
  const inline = csrfToken()
  if (inline) { _csrfCache = inline; return inline }
  try {
    const r = await fetch('/csrf-token', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
    const d = await r.json()
    _csrfCache = (d && d.token) || ''
  } catch (e) {
    _csrfCache = ''
  }
  return _csrfCache
}

export default function setupBasket () {
  setupQtySteppers()
  setupAddToBasket()
  setupTasterModal()
  // Paint immediately from localStorage cache for fast first render…
  updateHeaderCount()
  // …then fetch the real count and reconcile. Critical for the
  // post-order case: the basket has cleared server-side but the
  // localStorage cache still holds the pre-order count, so the badge
  // would otherwise stay at "1" (or whatever) indefinitely until the
  // user added something else. See issues.md "Header basket count
  // badge stays at '1' indefinitely after order completion".
  //
  // #261: ONLY reconcile when a session already exists. A cold anonymous
  // visitor has no server basket, so the localStorage badge is authoritative
  // — and firing /api/basket/count would mint a PHPSESSID cookie that defeats
  // the anonymous public cache (nginx micro-cache + Cache-Control max-age=120)
  // on every page after the first. (The server also returns 0 without starting
  // a session, but skipping the request keeps the page fully cookieless.)
  if (hasSessionCookie()) {
    hydrateHeaderCountFromServer()
  }
  setupBasketPage()
  setupBasketBar()
  setupCrossTabSync()
  setupLogoutBasketClear()
}

// Logout drops the server-side basket, so clear the client cache the instant
// the user clicks Log Out — otherwise the stale count lingers (or flashes) on
// the login page they're redirected to, and a cookieless landing never
// hydrates it away.
function setupLogoutBasketClear () {
  document.addEventListener('click', function (e) {
    const link = e.target && e.target.closest && e.target.closest('a[href="/account/logout"]')
    if (!link) return
    try {
      localStorage.removeItem(BASKET_COUNT_KEY)
      localStorage.removeItem(BASKET_TS_KEY)
      localStorage.removeItem(MULTIBUY_KEY)
      localStorage.removeItem(BASKET_SHOWS_KEY)
    } catch {}
    updateHeaderBadge(0)
  })
}

// #261: true only once a server session exists (a returning guest with a
// basket, or a logged-in customer). A fresh anonymous visitor has none, so we
// skip the count reconcile to keep their pages cookieless + publicly cached.
function hasSessionCookie () {
  try {
    return /(?:^|;\s*)PHPSESSID=/.test(document.cookie)
  } catch {
    return true // can't tell → behave as before (reconcile)
  }
}

async function hydrateHeaderCountFromServer () {
  try {
    const res = await fetch('/api/basket/count', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    const count = typeof data.count === 'number' ? data.count : 0
    saveBasketCount(count)
    updateHeaderBadge(count)
  } catch {
    // Network failure — leave the cached badge as-is. Better stale
    // than blank.
  }
}

// ─── localStorage basket count with timestamp (best-effort header badge cache) ──

const BASKET_COUNT_KEY = 'fa_basket_count'
const BASKET_TS_KEY = 'fa_basket_ts'
const STALE_MINUTES = 15

function saveBasketCount (n) {
  try {
    localStorage.setItem(BASKET_COUNT_KEY, String(n))
    localStorage.setItem(BASKET_TS_KEY, String(Date.now()))
  } catch {}
}

function loadBasketCount () {
  try {
    const count = parseInt(localStorage.getItem(BASKET_COUNT_KEY) || '0', 10)
    const ts = parseInt(localStorage.getItem(BASKET_TS_KEY) || '0', 10)
    const age = (Date.now() - ts) / 1000 / 60
    // If older than STALE_MINUTES, treat as unknown
    if (ts > 0 && age > STALE_MINUTES) return { count, stale: true }
    return { count, stale: false }
  } catch {
    return { count: 0, stale: false }
  }
}

// ─── Multibuy flag (localStorage) ────────────────────────────────

const MULTIBUY_KEY = 'fa_basket_has_multibuy'
const BASKET_SHOWS_KEY = 'fa_basket_show_ids'

function setMultibuyFlag () {
  try { localStorage.setItem(MULTIBUY_KEY, '1') } catch {}
}

function clearMultibuyFlag () {
  try { localStorage.removeItem(MULTIBUY_KEY) } catch {}
}

function addBasketShowId (showId) {
  if (!showId) return
  try {
    const ids = JSON.parse(localStorage.getItem(BASKET_SHOWS_KEY) || '[]')
    if (!ids.includes(showId)) ids.push(showId)
    localStorage.setItem(BASKET_SHOWS_KEY, JSON.stringify(ids))
  } catch {}
}

function clearBasketShowIds () {
  try { localStorage.removeItem(BASKET_SHOWS_KEY) } catch {}
}

// ─── Cross-tab basket count sync (#8) ────────────────────────────

function setupCrossTabSync () {
  window.addEventListener('storage', (e) => {
    if (e.key === BASKET_COUNT_KEY) {
      const n = parseInt(e.newValue || '0', 10)
      updateHeaderBadge(n, false)
    }
  })
}

// ─── Qty stepper (+/- buttons) ───────────────────────────────────

function setupQtySteppers () {
  document.querySelectorAll('.qty-stepper').forEach(stepper => {
    const minus = stepper.querySelector('.qty-stepper__minus')
    const plus = stepper.querySelector('.qty-stepper__plus')
    const display = stepper.querySelector('.qty-stepper__value')
    if (!minus || !plus || !display) return

    minus.addEventListener('click', () => {
      const val = parseInt(display.textContent, 10) || 1
      if (val > 1) display.textContent = val - 1
    })

    plus.addEventListener('click', () => {
      const val = parseInt(display.textContent, 10) || 1
      if (val < 10) display.textContent = val + 1
    })
  })
}

// ─── Add to basket ───────────────────────────────────────────────

function setupAddToBasket () {
  document.querySelectorAll('[data-action="add-to-basket"]').forEach(btn => {
    btn.addEventListener('click', () => handleAddToBasket(btn))
  })
}

async function handleAddToBasket (btn) {
  const showDateId = btn.dataset.showDateId
  if (!showDateId || btn.disabled) return

  // Get qty and selected ticket type from the sibling controls
  const row = btn.closest('.show-date-row__cta') || btn.parentElement
  const display = row.querySelector('.qty-stepper__value')
  const qty = display ? parseInt(display.textContent, 10) || 1 : 1
  const typeSelect = row.querySelector('.ticket-type-select')
  const selectedTypeId = typeSelect ? typeSelect.value : null

  // Disable button while working
  btn.disabled = true
  const originalText = btn.textContent
  btn.textContent = 'Adding\u2026'

  try {
    // Ensure a CSRF token is available before the add POST below \u2014 on a fresh
    // (cacheable, sessionless) show page this lazily fetches+caches one.
    await ensureToken()

    // 1. Get Spektrix IDs + ticket types from our backend
    const optRes = await fetch(`/api/basket/options/${showDateId}`)
    const optData = await optRes.json()

    if (!optRes.ok || !optData.ticketTypes || optData.ticketTypes.length === 0) {
      showToast(optData.error || 'No ticket types available', 'error')
      return
    }

    // #10 — Check live availability before attempting to add
    const { instanceId, planId, ticketTypes, status } = optData
    if (status && status.available !== undefined && status.available <= 0) {
      btn.textContent = 'Sold Out'
      btn.disabled = true
      btn.classList.add('show-date-row__book--sold-out')
      const stepper = row.querySelector('.qty-stepper')
      if (stepper) stepper.style.display = 'none'
      showToast('Sorry, this show has just sold out', 'error')
      return
    }
    // Per-type availability: mark sold-out types in the dropdown (so a seated
    // sellout doesn't hide the standing tickets still on sale), then validate
    // the chosen type against its OWN pool, not the combined total.
    if (typeSelect) reconcileTypeAvailability(typeSelect, ticketTypes)

    // The buyer's pick has sold out since the (cached) page rendered, and
    // reconcile moved the dropdown to a type still on sale. Never add that
    // other type on their behalf (#1032) — say so and let them choose again.
    if (typeSelect && selectedTypeId && typeSelect.value !== selectedTypeId) {
      const gone = ticketTypes.find(t => String(t.id) === String(selectedTypeId))
      const open = ticketTypes.find(t => String(t.id) === String(typeSelect.value))
      const goneName = gone ? gone.name : 'That ticket type'
      showToast(open
        ? `${goneName} is sold out for this performance \u2014 ${open.name} tickets are still available`
        : `${goneName} is sold out for this performance`, 'error')
      return
    }

    const typeId = (typeSelect ? typeSelect.value : selectedTypeId) || ticketTypes[0].id
    const chosen = ticketTypes.find(t => String(t.id) === String(typeId)) || ticketTypes[0]

    if (chosen.soldOut || (chosen.available !== undefined && chosen.available <= 0)) {
      showToast(`${chosen.name} is sold out for this performance`, 'error')
      return
    }
    if (chosen.available !== undefined && qty > chosen.available) {
      showToast(`Only ${chosen.available} ${chosen.name} ticket${chosen.available === 1 ? '' : 's'} left`, 'error')
      return
    }

    // 2. Add tickets via our server-side basket (System Owner API).
    const addRes = await fetch('/api/basket/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() },
      body: JSON.stringify({
        instanceId,
        planId,
        ticketTypeId: typeId,
        qty
      })
    })

    if (!addRes.ok) {
      const err = await addRes.json().catch(() => ({}))
      showToast(err.error || 'Could not add to basket', 'error')
      return
    }

    const addData = await addRes.json()

    // 3. Update count from server response
    const newCount = addData.count || 0
    saveBasketCount(newCount)
    updateHeaderCount(newCount)

    // Track if basket contains a multibuy-eligible item
    if (optData.multibuyDiscount) setMultibuyFlag()
    if (optData.showId) addBasketShowId(optData.showId)

    // Reset qty back to 1
    if (display) display.textContent = '1'

    // #9 — Include fee info in toast
    const selectedType = ticketTypes.find(t => t.id === typeId) || ticketTypes[0]
    const label = qty > 1 ? `${qty} tickets` : '1 ticket'
    const feeTotal = (selectedType.bookingFee || 0) * qty
    const feeNote = feeTotal > 0 ? ` (incl. \u00A3${feeTotal.toFixed(2)} fees)` : ''
    showToast(`${label} added to basket${feeNote}`, 'success')

    // Basket contents changed — invalidate cached cross-sell suggestions
    // (the basket bar / basket page re-fetch them on render)
    clearCrossSellCache()

    // Refresh the persistent basket bar with new data
    refreshBasketBar()
  } catch (err) {
    console.error('Add to basket error:', err)
    showToast('Something went wrong. Please try again', 'error')
  } finally {
    // #10 — Don't re-enable if permanently disabled by sold-out
    if (!btn.classList.contains('show-date-row__book--sold-out')) {
      btn.disabled = false
      btn.textContent = originalText
    }
  }
}

// Reflect per-type availability from /options onto the <select>: disable
// sold-out types (so a seated sellout doesn't let you pick a dead type while
// standing is still on sale) and move the selection off a type that has just
// sold out. Member-price teaser rows (disabled, non-numeric value) are skipped.
function reconcileTypeAvailability (typeSelect, ticketTypes) {
  const byId = new Map(ticketTypes.map(t => [String(t.id), t]))
  const suffix = ' (sold out)'
  let selectedGone = false
  typeSelect.querySelectorAll('option').forEach(opt => {
    const t = byId.get(String(opt.value))
    if (!t) return
    const soldOut = !!t.soldOut || (t.available !== undefined && t.available <= 0)
    opt.disabled = soldOut
    const hasSuffix = opt.textContent.endsWith(suffix)
    if (soldOut && !hasSuffix) opt.textContent += suffix
    else if (!soldOut && hasSuffix) opt.textContent = opt.textContent.slice(0, -suffix.length)
    if (soldOut && opt.selected) selectedGone = true
  })
  if (selectedGone) {
    const firstOpen = Array.from(typeSelect.options).find(o => o.value && !o.disabled)
    if (firstOpen) typeSelect.value = firstOpen.value
  }
}

// ─── Taster booking modal ────────────────────────────────────────
// Triggered by .c3__book[data-taster-id] (or any element with the attribute).
// Fetches /api/basket/taster-options/{id}, renders a modal with ticket type
// radios + qty input, posts to /api/basket/add, then redirects to /purchase/basket.

function setupTasterModal () {
  // Intercept any link/button carrying data-taster-id and open the modal.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-taster-id]')
    if (!trigger) return
    e.preventDefault()
    openTasterModal(trigger.dataset.tasterId)
  })
}

function ensureTasterModal () {
  let modal = document.getElementById('taster-modal')
  if (modal) return modal

  modal = document.createElement('div')
  modal.id = 'taster-modal'
  modal.className = 'taster-modal'
  modal.hidden = true
  modal.innerHTML = `
    <div class="taster-modal__backdrop" data-close></div>
    <div class="taster-modal__panel" role="dialog" aria-modal="true" aria-labelledby="taster-modal-title">
      <button class="taster-modal__close" data-close aria-label="Close">&times;</button>
      <div class="taster-modal__body" data-modal-body>
        <p class="taster-modal__loading">Loading…</p>
      </div>
    </div>
  `
  document.body.appendChild(modal)

  // Close handlers
  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-close]')) closeTasterModal()
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) closeTasterModal()
  })

  return modal
}

function closeTasterModal () {
  const modal = document.getElementById('taster-modal')
  if (!modal) return
  modal.hidden = true
  document.body.style.overflow = ''
}

async function openTasterModal (tasterId) {
  const modal = ensureTasterModal()
  const body = modal.querySelector('[data-modal-body]')
  body.innerHTML = '<p class="taster-modal__loading">Loading…</p>'
  modal.hidden = false
  document.body.style.overflow = 'hidden'

  try {
    const res = await fetch(`/api/basket/taster-options/${encodeURIComponent(tasterId)}`)
    const data = await res.json()

    if (!res.ok || !data.ticketTypes || data.ticketTypes.length === 0) {
      body.innerHTML = `
        <p class="taster-modal__error">${escapeHtml(data.error || 'Booking unavailable for this class.')}</p>
        <button type="button" class="button button--pink" data-close>Close</button>
      `
      return
    }

    const available = data.status && data.status.available
    if (typeof available === 'number' && available <= 0) {
      body.innerHTML = `
        <h2 class="taster-modal__title" id="taster-modal-title">${escapeHtml(data.tasterName || 'Taster class')}</h2>
        <p class="taster-modal__date">${escapeHtml(data.tasterDate || '')}</p>
        <p class="taster-modal__error">Sorry, this class is fully booked.</p>
        <button type="button" class="button button--pink" data-close>Close</button>
      `
      return
    }

    renderTasterModalForm(body, data)
  } catch (err) {
    console.error('Taster modal error:', err)
    body.innerHTML = `
      <p class="taster-modal__error">Couldn't load booking details. Please try again.</p>
      <button type="button" class="button button--pink" data-close>Close</button>
    `
  }
}

function renderTasterModalForm (body, data) {
  const ticketTypeOptions = data.ticketTypes.map((t, i) => `
    <label class="taster-modal__type">
      <input type="radio" name="ticketType" value="${escapeHtml(t.id)}" ${i === 0 ? 'checked' : ''}>
      <span class="taster-modal__type-name">${escapeHtml(t.name)}</span>
      <span class="taster-modal__type-price">£${t.price.toFixed(2)}</span>
    </label>
  `).join('')

  body.innerHTML = `
    <h2 class="taster-modal__title" id="taster-modal-title">${escapeHtml(data.tasterName || 'Book this class')}</h2>
    <p class="taster-modal__date">${escapeHtml(data.tasterDate || '')}</p>

    <form class="taster-modal__form" data-taster-form>
      <fieldset class="taster-modal__fieldset">
        <legend class="taster-modal__legend">Ticket type</legend>
        <div class="taster-modal__types">${ticketTypeOptions}</div>
      </fieldset>

      <div class="taster-modal__qty-row">
        <label for="taster-modal-qty" class="taster-modal__legend">Quantity</label>
        <div class="qty-stepper">
          <button type="button" class="qty-stepper__btn qty-stepper__minus" aria-label="Decrease">&minus;</button>
          <span class="qty-stepper__value">1</span>
          <button type="button" class="qty-stepper__btn qty-stepper__plus" aria-label="Increase">&plus;</button>
        </div>
      </div>

      <button type="submit" class="button button--pink taster-modal__submit">Add to basket</button>
    </form>
  `

  // Wire qty stepper inside the modal
  const minus = body.querySelector('.qty-stepper__minus')
  const plus = body.querySelector('.qty-stepper__plus')
  const display = body.querySelector('.qty-stepper__value')
  minus?.addEventListener('click', () => {
    const v = parseInt(display.textContent, 10) || 1
    if (v > 1) display.textContent = v - 1
  })
  plus?.addEventListener('click', () => {
    const v = parseInt(display.textContent, 10) || 1
    if (v < 10) display.textContent = v + 1
  })

  // Submit handler
  const form = body.querySelector('[data-taster-form]')
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    submitTasterBooking(form, data)
  })
}

async function submitTasterBooking (form, data) {
  const submit = form.querySelector('.taster-modal__submit')
  const original = submit.textContent
  const typeId = form.querySelector('input[name="ticketType"]:checked')?.value
  const qty = parseInt(form.querySelector('.qty-stepper__value').textContent, 10) || 1

  if (!typeId) return

  submit.disabled = true
  submit.textContent = 'Adding…'

  try {
    // Prime the CSRF token first (taster pages are cacheable/sessionless).
    await ensureToken()
    const res = await fetch('/api/basket/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Security-Token': csrfToken()
      },
      body: JSON.stringify({
        instanceId: data.instanceId,
        planId: data.planId,
        ticketTypeId: typeId,
        qty
      })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.error || 'Could not add to basket', 'error')
      submit.disabled = false
      submit.textContent = original
      return
    }

    const body = await res.json()
    saveBasketCount(body.count || 0)
    updateHeaderCount(body.count || 0)

    closeTasterModal()
    window.location.href = '/purchase/basket'
  } catch (err) {
    console.error('Taster add error:', err)
    showToast('Something went wrong. Please try again', 'error')
    submit.disabled = false
    submit.textContent = original
  }
}

// ─── Header basket count badge ───────────────────────────────────

/** Update the badge element directly (no localStorage read). */
function updateHeaderBadge (count) {
  const badge = document.querySelector('[data-basket-count]')
  if (!badge) return

  // Always show the number (or nothing). A stale cached count self-corrects on
  // the next server hydrate; we no longer flash a confusing "?" placeholder.
  badge.textContent = count > 0 ? count : ''
  badge.classList.toggle('basket-count--visible', count > 0)
}

function updateHeaderCount (count) {
  // If a count was passed in, use it directly (fresh) and persist to
  // localStorage so the next page load doesn't display a stale value.
  // Saving on every fresh count means callers don't have to remember to
  // saveBasketCount() separately — single source of truth.
  if (typeof count === 'number') {
    saveBasketCount(count)
    updateHeaderBadge(count)
    return
  }

  // Otherwise use the localStorage cache — a stale value self-corrects on the
  // next server hydrate (hasSessionCookie path), no "?" placeholder.
  const { count: cached } = loadBasketCount()
  updateHeaderBadge(cached)
}

// ─── Toast notification ──────────────────────────────────────────

let toastTimeout = null

function showToast (message, type = 'success') {
  // Remove existing toast
  const existing = document.querySelector('.basket-toast')
  if (existing) existing.remove()
  if (toastTimeout) clearTimeout(toastTimeout)

  const toast = document.createElement('div')
  toast.className = `basket-toast basket-toast--${type}`
  toast.textContent = message
  document.body.appendChild(toast)

  // Lift the toast clear of the fixed basket bar — measured, because the
  // bar's height varies (cross-sell strip). Re-measured after the bar has
  // had a chance to (re)render following an add-to-basket.
  const positionToast = () => {
    const bar = document.querySelector('.basket-bar')
    if (!bar) return
    // offsetParent is null for position:fixed elements — gauge visibility
    // from the rect instead
    const rect = bar.getBoundingClientRect()
    if (rect.height > 0 && rect.top < window.innerHeight) {
      toast.style.bottom = `${Math.max(0, window.innerHeight - rect.top) + 12}px`
    }
  }
  positionToast()
  setTimeout(positionToast, 400)

  // Trigger reflow then add visible class for animation
  toast.offsetHeight // eslint-disable-line no-unused-expressions
  toast.classList.add('basket-toast--visible')

  toastTimeout = setTimeout(() => {
    toast.classList.remove('basket-toast--visible')
    setTimeout(() => toast.remove(), 300)
  }, 3500)
}

// ═════════════════════════════════════════════════════════════════
// BASKET PAGE — /purchase/basket
// ═════════════════════════════════════════════════════════════════

function setupBasketPage () {
  const page = document.querySelector('[data-basket-page]')
  if (!page) return // Not on the basket page

  fetchAndRenderBasket(page)

  // Clear basket button
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.js-clear-basket')
    if (!btn || btn.disabled) return

    if (!confirm('Remove all items from your basket?')) return

    btn.disabled = true
    btn.textContent = 'Clearing\u2026'

    try {
      const res = await fetch('/api/basket/clear', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
      })

      if (res.ok) {
        saveBasketCount(0)
        updateHeaderCount(0)
        clearCrossSellCache()
        fetchAndRenderBasket(page)
        showToast('Basket cleared', 'success')
      } else {
        btn.disabled = false
        btn.textContent = 'Clear Basket'
        showToast('Could not clear basket', 'error')
      }
    } catch (err) {
      console.error('Clear basket error:', err)
      btn.disabled = false
      btn.textContent = 'Clear Basket'
      showToast('Something went wrong', 'error')
    }
  })

  // Submit promo code on Enter key
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return
    const input = e.target.closest('[data-promo-input]')
    if (!input) return
    e.preventDefault()
    const btn = document.querySelector('.js-apply-promo')
    if (btn && !btn.disabled) btn.click()
  })

  // Apply promo code
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.js-apply-promo')
    if (!btn || btn.disabled) return

    const input = document.querySelector('[data-promo-input]')
    const errorEl = document.querySelector('[data-promo-error]')
    const code = input ? input.value.trim() : ''

    const setPromoError = (msg) => {
      if (!errorEl) return
      errorEl.textContent = msg
      errorEl.hidden = false
    }
    const clearPromoError = () => {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true }
    }

    // Map raw API error strings to user-friendly copy. Mirrors the
    // pattern used in custom-checkout.js applyPromo().
    const friendlyPromoError = (raw) => {
      if (!raw) return 'We couldn’t apply that code. Please try again.'
      const lower = String(raw).toLowerCase()
      if (lower.includes('not found') || lower.includes('invalid') || lower.includes('expired')) {
        return 'We couldn’t find that code. Please check and try again.'
      }
      if (lower.includes('already used')) return 'You have already used this code.'
      return raw
    }

    clearPromoError()
    if (!code) {
      setPromoError('Please enter a discount code')
      return
    }

    btn.disabled = true
    btn.textContent = 'Applying…'

    try {
      const res = await fetch('/api/basket/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() },
        body: JSON.stringify({ code })
      })

      const data = await res.json().catch(() => ({}))

      if (res.ok) {
        if (data.warning) {
          // Code accepted by Spektrix but didn't move the discount
          setPromoError(data.warning)
        } else {
          showToast('Discount code applied', 'success')
        }
        fetchAndRenderBasket(page)
      } else {
        setPromoError(friendlyPromoError(data.error))
      }
    } catch (err) {
      console.error('Promo code error:', err)
      setPromoError('Something went wrong. Please try again.')
    } finally {
      btn.disabled = false
      btn.textContent = 'Apply'
    }
  })

  // Remove promo code
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.js-remove-promo')
    if (!btn || btn.disabled) return

    btn.disabled = true

    try {
      const res = await fetch('/api/basket/promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() },
        body: JSON.stringify({ code: '' })
      })

      if (res.ok) {
        showToast('Discount code removed', 'success')
        fetchAndRenderBasket(page)
      } else {
        showToast('Could not remove discount code', 'error')
      }
    } catch (err) {
      console.error('Remove promo error:', err)
      showToast('Something went wrong', 'error')
    } finally {
      btn.disabled = false
    }
  })

  // Apply / remove free ticket
  document.addEventListener('click', async (e) => {
    const applyBtn = e.target.closest('.js-apply-free-ticket')
    const removeBtn = e.target.closest('.js-remove-free-ticket')
    const btn = applyBtn || removeBtn
    if (!btn || btn.disabled) return
    const promotionId = applyBtn ? Number(btn.dataset.promotionId) : 0
    const originalLabel = btn.textContent
    btn.disabled = true
    btn.textContent = applyBtn ? 'Applying…' : '…'

    try {
      const res = await fetch('/api/basket/free-ticket', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Security-Token': csrfToken(),
        },
        body: JSON.stringify({ promotionId }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        showToast(applyBtn ? 'Free ticket applied' : 'Free ticket removed', 'success')
        fetchAndRenderBasket(page)
        return
      }
      showToast((data && data.error) || 'Could not update your free ticket', 'error')
    } catch (err) {
      console.error('Free ticket error:', err)
      showToast('Something went wrong. Please try again.', 'error')
    } finally {
      btn.disabled = false
      btn.textContent = originalLabel
    }
  })

  // #204: move the free ticket to the chosen line — or, via the "Save for
  // later" option, decline it (the saved box persists so it can be re-applied).
  document.addEventListener('change', async (e) => {
    const sel = e.target.closest('.js-free-ticket-target')
    if (!sel || sel.disabled) return
    const saveForLater = String(sel.value) === 'save'
    const [targetShowDateId, targetTicketTypeId] = saveForLater
      ? [0, 0]
      : String(sel.value).split(':').map(Number)
    const promotionId = saveForLater ? 0 : Number(sel.dataset.promotionId)
    sel.disabled = true
    try {
      const res = await fetch('/api/basket/free-ticket', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Security-Token': csrfToken(),
        },
        body: JSON.stringify({ promotionId, targetShowDateId, targetTicketTypeId }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        showToast(saveForLater ? 'Free ticket saved for later' : 'Free ticket moved', 'success')
        fetchAndRenderBasket(page)
        return
      }
      showToast((data && data.error) || 'Could not update your free ticket', 'error')
      sel.disabled = false
    } catch (err) {
      console.error('Free ticket target error:', err)
      showToast('Something went wrong. Please try again.', 'error')
      sel.disabled = false
    }
  })
}

async function fetchAndRenderBasket (page) {
  const loadingEl = page.querySelector('[data-basket-loading]')
  const emptyEl = page.querySelector('[data-basket-empty]')
  const contentEl = page.querySelector('[data-basket-content]')
  const expiredEl = page.querySelector('[data-basket-expired]')

  try {
    // Fetch the enriched basket in one call — the server does the
    // Spektrix fetch, groups tickets, enriches with show data, and
    // computes totals.
    const res = await fetch('/api/basket/get')
    if (!res.ok) throw new Error(`Basket fetch failed: ${res.status}`)

    const data = await res.json()
    if (loadingEl) loadingEl.style.display = 'none'

    // Expired basket
    if (data.expired) {
      if (contentEl) contentEl.style.display = 'none'
      if (emptyEl) emptyEl.style.display = 'none'
      if (expiredEl) expiredEl.style.display = ''
      saveBasketCount(0)
      updateHeaderCount(0)
      return
    }

    const hasTickets = data.grouped && data.grouped.length > 0
    const hasMemberships = data.memberships && data.memberships.length > 0
    const hasGiftVouchers = data.giftVouchers && data.giftVouchers.length > 0

    // Empty basket
    if (!hasTickets && !hasMemberships && !hasGiftVouchers) {
      saveBasketCount(0)
      clearMultibuyFlag()
      clearBasketShowIds()
      updateHeaderCount(0)
      if (contentEl) contentEl.style.display = 'none'
      if (expiredEl) expiredEl.style.display = 'none'
      if (emptyEl) emptyEl.style.display = ''
      return
    }

    // Update count cache from actual data (gift vouchers don't count towards "ticket" count in header)
    const totalCount = (data.count || 0) + (data.giftVouchers ? data.giftVouchers.length : 0)
    saveBasketCount(totalCount)
    updateHeaderCount(totalCount)

    // Render items
    renderBasketItems(page, data)
    renderMemberDiscountBanner(page, data)
    renderJoinAndBookUpsell(page, data)
    if (contentEl) contentEl.style.display = ''

    // Start/update basket hold timer
    if (data.holdExpiresAt) {
      startBasketTimer(page, data.holdExpiresAt)
    }

    // Fetch and render cross-sell row on basket page. Always called so a
    // stale suggestion is cleared when the last suggesting line is removed
    // (e.g. a membership/voucher-only basket).
    const instanceIds = extractInstanceIds(data.grouped)
    if (instanceIds.length > 0) {
      fetchCrossSells(instanceIds).then(cs => renderCrossSellRow(page, cs))
    } else {
      renderCrossSellRow(page, [])
    }
  } catch (err) {
    console.error('Basket page fetch error:', err)
    if (loadingEl) loadingEl.style.display = 'none'
    if (contentEl) contentEl.style.display = 'none'
    if (expiredEl) expiredEl.style.display = 'none'
    if (emptyEl) emptyEl.style.display = ''
    saveBasketCount(0)
    updateHeaderCount(0)
  }
}

function renderBasketItems (page, data) {
  const container = page.querySelector('[data-basket-items]')
  if (!container) return

  // Render membership items first
  const membershipHtml = (data.memberships || []).map(m => {
    const img = m.image
      ? `<img class="basket-item__image" src="${escapeHtml(m.image)}" alt="">`
      : `<div class="basket-item__image basket-item__image--placeholder">
           <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
         </div>`

    // Welcome offer (discounted first month). It's a Stripe coupon on the first
    // invoice, not a basket line, so without this the line quotes the full price
    // and the offer the upsell just promised looks withdrawn.
    const offer = data.membershipOffer
    const offerFirstMonth = offer ? (m.price * (100 - offer.percent)) / 100 : 0
    const offerNote = offer
      ? `<div class="basket-item__meta">First month, then &pound;${m.price.toFixed(2)}/month</div>`
      : ''
    const membershipTotal = offer
      ? `<s class="basket-item__line-total-was">&pound;${m.price.toFixed(2)}</s> &pound;${offerFirstMonth.toFixed(2)}`
      : `&pound;${m.price.toFixed(2)}`

    return `
      <div class="basket-item basket-item--membership" data-basket-membership="${escapeHtml(m.id)}">
        <div class="basket-item__poster">${img}</div>
        <div class="basket-item__details">
          <span class="basket-item__show-name">${escapeHtml(m.name)}</span>
          ${offerNote}
          <div class="basket-item__ticket-type">Membership</div>
        </div>
        <div class="basket-item__qty">1 &times;</div>
        <div class="basket-item__line-total">${membershipTotal}</div>
        <div class="basket-item__actions">
          <button class="basket-item__remove-line" data-remove-membership="${escapeHtml(m.id)}" title="Remove" aria-label="Remove">${TRASH_ICON}</button>
        </div>
      </div>
    `
  }).join('')

  // Render gift voucher items
  const giftVoucherHtml = (data.giftVouchers || []).map(gv => {
    const deliveryNote = gv.deliveryEmailAddress
      ? `To: ${escapeHtml(gv.toName)} (${escapeHtml(gv.deliveryEmailAddress)})`
      : `To: ${escapeHtml(gv.toName)}`

    return `
      <div class="basket-item basket-item--gift-voucher" data-basket-gift-voucher="${escapeHtml(gv.id)}">
        <div class="basket-item__poster">
          <div class="basket-item__image basket-item__image--placeholder">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
          </div>
        </div>
        <div class="basket-item__details">
          <span class="basket-item__show-name">Gift Voucher</span>
          <div class="basket-item__meta">${deliveryNote}</div>
          ${gv.message ? `<div class="basket-item__ticket-type">&ldquo;${escapeHtml(gv.message)}&rdquo;</div>` : ''}
        </div>
        <div class="basket-item__qty">1 &times;</div>
        <div class="basket-item__line-total">&pound;${gv.amount.toFixed(2)}</div>
        <div class="basket-item__actions">
          <button class="basket-item__remove-line" data-remove-gift-voucher="${escapeHtml(gv.id)}" title="Remove" aria-label="Remove">${TRASH_ICON}</button>
        </div>
      </div>
    `
  }).join('')

  // Render ticket items
  container.innerHTML = membershipHtml + giftVoucherHtml + data.grouped.map(line => {
    const img = line.showImage
      ? `<img class="basket-item__image" src="${escapeHtml(line.showImage)}" alt="">`
      : `<div class="basket-item__image basket-item__image--placeholder">
           <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 21V3M16 21V3M2 12h20"/></svg>
         </div>`

    // One Remove for the whole line — the qty stepper's [-] covers "remove 1".
    const removeHtml = line.qty === 1
      ? `<button class="basket-item__remove-line" data-remove-ticket="${escapeHtml(line.ticketIds[0])}" title="Remove" aria-label="Remove">${TRASH_ICON}</button>`
      : `<button class="basket-item__remove-line" data-remove-all="${escapeHtml(JSON.stringify(line.ticketIds))}" title="Remove" aria-label="Remove">${TRASH_ICON}</button>`

    // Every line shows its real discounted TOTAL (server emits lineFullTotal +
    // originalLineTotal), so member %, multibuy % and free tickets all render
    // consistently — struck-through where discounted, "Free" where the whole
    // line is covered.
    const lineNow = (typeof line.lineFullTotal === 'number')
      ? line.lineFullTotal
      : ((line.lineTotal || 0) + (line.lineBookingFees || 0))
    const lineWas = line.originalLineTotal || 0
    const hasStrikethrough = lineWas > lineNow + 0.01
    // A free line reads "Free". Show the struck-through "was" price only when a
    // discount took it to £0 (lineWas set) — a free-by-nature ticket (£0 base,
    // no discount) has no "was" price, so just "Free" with no strike-through.
    const lineTotalCell = line.lineIsFree
      ? (hasStrikethrough
        ? `<s class="basket-item__line-total-was">&pound;${lineWas.toFixed(2)}</s> <span class="basket-item__free-price">Free</span>`
        : `<span class="basket-item__free-price">Free</span>`)
      : (hasStrikethrough
        ? `<s class="basket-item__line-total-was">&pound;${lineWas.toFixed(2)}</s> &pound;${lineNow.toFixed(2)}`
        : `&pound;${lineNow.toFixed(2)}`)

    const freeBadge = (line.freeTicketCount > 0)
      ? `<span class="basket-item__free-badge">${line.freeTicketCount} free</span>`
      : ''

    // Access lines can add a free carer companion — one per access seat. The
    // server (companionAddable) says how many more are addable; the link posts
    // to /api/basket/companion for this performance and re-renders.
    const companionName = line.companionTypeName || 'carer'
    const companionHtml = (line.companionAddable > 0)
      ? `<button type="button" class="basket-item__add-companion" data-add-companion="${escapeHtml(String(line.showDateId))}">+ Add a free ${escapeHtml(companionName.toLowerCase())} ticket</button>`
      : ''

    return `
      <div class="basket-item" data-basket-line="${escapeHtml(line.key)}">
        <div class="basket-item__poster">${img}</div>
        <div class="basket-item__details">
          <a class="basket-item__show-name" href="${escapeHtml(line.showLink)}">${escapeHtml(line.showName)}</a>
          <div class="basket-item__meta">${escapeHtml(line.showDate)}</div>
          <div class="basket-item__ticket-type">${escapeHtml(line.ticketType)} ${freeBadge}</div>
          ${companionHtml}
        </div>
        <div class="basket-item__qty basket-item__qty--stepper">
          <button type="button" class="basket-item__qty-btn" data-qty-dec="${escapeHtml(line.ticketIds[line.ticketIds.length - 1])}" aria-label="Remove one ticket">&minus;</button>
          <span class="basket-item__qty-count">${line.qty}</span>
          <button type="button" class="basket-item__qty-btn" data-qty-inc="${escapeHtml(String(line.showDateId))}:${escapeHtml(String(line.ticketTypeId))}" aria-label="Add one ticket">+</button>
        </div>
        <div class="basket-item__line-total">${lineTotalCell}</div>
        <div class="basket-item__actions">${removeHtml}</div>
      </div>
    `
  }).join('')

  // Update summary
  updateBasketSummary(page, data)

  // Wire up remove buttons
  container.querySelectorAll('[data-remove-ticket]').forEach(btn => {
    btn.addEventListener('click', () => handleRemoveTicket(btn, page))
  })
  container.querySelectorAll('[data-remove-all]').forEach(btn => {
    btn.addEventListener('click', () => handleRemoveAll(btn, page))
  })

  // Wire up the qty steppers (+ adds one more seat, - releases one)
  container.querySelectorAll('[data-qty-inc]').forEach(btn => {
    btn.addEventListener('click', () => handleQtyInc(btn, page))
  })
  container.querySelectorAll('[data-qty-dec]').forEach(btn => {
    btn.addEventListener('click', () => handleQtyDec(btn, page))
  })

  // Wire up "add free carer ticket" links
  container.querySelectorAll('[data-add-companion]').forEach(btn => {
    btn.addEventListener('click', () => handleAddCompanion(btn, page))
  })

  // Wire up membership remove buttons
  container.querySelectorAll('[data-remove-membership]').forEach(btn => {
    btn.addEventListener('click', () => handleRemoveMembership(btn, page))
  })

  // Wire up gift voucher remove buttons
  container.querySelectorAll('[data-remove-gift-voucher]').forEach(btn => {
    btn.addEventListener('click', () => handleRemoveGiftVoucher(btn, page))
  })
}

// N40: show the "log in for your member discount" banner only when the server
// flags the basket as member-discount-eligible AND the visitor isn't logged
// in. The flag is server-computed (eligibleForMemberDiscount) — the server
// already knows the visitor is a guest, so a true value means "guest holding
// an eligible ticket". Hidden again on any basket change that clears it.
function renderMemberDiscountBanner (page, data) {
  const banner = page.querySelector('[data-member-discount-banner]')
  if (!banner) return
  banner.hidden = !data.eligibleForMemberDiscount
}

// Join & Book inline upsell. The server flags eligibility (combined flow on,
// no membership in basket, visitor not a member, a line benefits). Clicking
// "Add membership" drops the featured tier into the basket and re-renders —
// the basket then shows the conditional member quote. A guest is sent to log
// in first (subscriptions need an account).
function renderJoinAndBookUpsell (page, data) {
  const box = page.querySelector('[data-jnb-upsell]')
  if (!box) return
  const upsell = data.joinAndBookUpsell
  if (!upsell || !upsell.eligible) {
    box.hidden = true
    updateOffersBoxVisibility(page)
    return
  }
  box.hidden = false
  updateOffersBoxVisibility(page)

  // The upsell is the stronger CTA — suppress the N40 "log in for your member
  // discount" banner so a guest doesn't see two stacked pink login prompts.
  const memberBanner = page.querySelector('[data-member-discount-banner]')
  if (memberBanner) memberBanner.hidden = true

  const titleEl = box.querySelector('[data-jnb-upsell-title]')
  const textEl = box.querySelector('[data-jnb-upsell-text]')
  const action = box.querySelector('[data-jnb-upsell-add]')
  const iconEl = box.querySelector('[data-jnb-upsell-icon]')
  const pct = upsell.discountPercent || 0
  const price = (upsell.membershipPrice || 0).toFixed(2)
  const name = upsell.membershipName || 'FA Membership'
  const savings = upsell.savingsAmount || 0

  if (iconEl) {
    if (upsell.membershipImage) {
      iconEl.src = upsell.membershipImage
      iconEl.hidden = false
    } else {
      iconEl.hidden = true
    }
  }

  if (titleEl) {
    if (savings > 0) {
      // The server priced this basket as the member they'd become (member %
      // + auto free ticket) and diffed against the current quote.
      titleEl.textContent = `Join the FAM and save £${savings.toFixed(2)}`
    } else if (upsell.freeTicketApplies) {
      // A basket show is covered by the member free ticket — joining makes it
      // free, a stronger hook than the %.
      titleEl.textContent = upsell.freeShowTitle
        ? `Join the FAM and get ${upsell.freeShowTitle} free`
        : 'Join the FAM and get this show free'
    } else if (pct > 0) {
      titleEl.textContent = `Join the FAM and save ${pct}% on these tickets`
    } else {
      titleEl.textContent = 'Join the FAM and save on these tickets'
    }
  }
  if (textEl) {
    let text = upsell.freeTicketApplies
      ? `Add ${name} for £${price}. Your free monthly ticket applies the moment you join, all in one checkout.`
      : `Add ${name} for £${price}. Your member price applies the moment you join, all in one checkout.`
    // Welcome offer (discounted first month) — append when the server says the
    // offer is live and has slots. offerFirstMonthPrice is the first invoice;
    // the £price above recurs after.
    if (upsell.offerPercent) {
      const firstMonth = (upsell.offerFirstMonthPrice || 0).toFixed(2)
      text += ` Right now your first month is just £${firstMonth} (${upsell.offerPercent}% off).`
      if (upsell.offerLastFew) text += ' Last few remaining!'
    }
    textEl.textContent = text
  }
  if (!action) return

  // Anyone can add the membership — logged in or not. Details are captured at
  // checkout (an account is created there), so no forced login here.
  action.textContent = 'Add membership'
  action.setAttribute('href', '#')
  action.dataset.membershipId = String(upsell.membershipId)
  if (!action.dataset.jnbBound) {
    action.dataset.jnbBound = '1'
    action.addEventListener('click', async (e) => {
      e.preventDefault()
      const id = action.dataset.membershipId
      if (!id) return
      action.setAttribute('aria-busy', 'true')
      try {
        await fetch('/api/basket/add-membership', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Security-Token': csrfToken()
          },
          body: JSON.stringify({ membershipId: Number(id) })
        })
      } catch (err) {
        // Swallow — the re-render reflects the true server state either way.
      }
      action.removeAttribute('aria-busy')
      fetchAndRenderBasket(page)
    })
  }
}

function updateBasketSummary (page, data) {
  const countEl = page.querySelector('[data-basket-summary-count]')
  const subtotalEl = page.querySelector('[data-basket-summary-subtotal]')
  const feesRow = page.querySelector('[data-basket-summary-fees-row]')
  const feesEl = page.querySelector('[data-basket-summary-fees]')
  const discountRow = page.querySelector('[data-basket-summary-discount-row]')
  const discountEl = page.querySelector('[data-basket-summary-discount]')
  const discountLabelEl = page.querySelector('[data-basket-summary-discount-label]')
  const totalEl = page.querySelector('[data-basket-summary-total]')

  if (countEl) {
    const gvCount = (data.giftVouchers || []).length
    const n = (data.count || 0) + gvCount
    const hasMixed = (data.memberships && data.memberships.length > 0) || gvCount > 0
    const label = hasMixed ? 'item' : 'ticket'
    countEl.textContent = n === 1 ? `1 ${label}` : `${n} ${label}s`
  }

  // Per-show ticket counts (+ any membership) under the Tickets heading, so
  // the summary reads Show × count rather than one opaque number.
  const breakdownEl = page.querySelector('[data-basket-summary-breakdown]')
  if (breakdownEl) {
    const byShow = new Map()
    for (const line of (data.grouped || [])) {
      const name = line.showName || 'Show'
      byShow.set(name, (byShow.get(name) || 0) + (line.qty || 0))
    }
    let html = ''
    for (const [name, qty] of byShow) {
      html += `<div class="basket-summary__breakdown-row"><span>${escapeHtml(name)}</span><span>${qty}</span></div>`
    }
    for (const m of (data.memberships || [])) {
      html += `<div class="basket-summary__breakdown-row"><span>${escapeHtml(m.name || 'Membership')}</span><span>1</span></div>`
    }
    breakdownEl.innerHTML = html
    breakdownEl.style.display = html ? '' : 'none'
  }

  // Subtotal — ticket/membership prices EXCLUDING booking fees; the fees
  // get their own "Service & Booking fee" row below the discount
  if (subtotalEl) {
    const s = data.subtotal || 0
    subtotalEl.textContent = `\u00A3${s.toFixed(2)}`
  }

  // Service & Booking fee — its own row, shown when fees > 0
  const fees = data.totalBookingFees || 0
  if (feesRow) feesRow.style.display = fees > 0 ? '' : 'none'
  if (feesEl) feesEl.textContent = `\u00A3${fees.toFixed(2)}`

  // Discount — show row only if > 0
  // The membership welcome offer is a first-month Stripe-coupon discount (not a
  // basket line), so it has its own row below \u2014 this one is ticket discounts.
  const discount = data.totalDiscount || 0
  if (discountRow) discountRow.style.display = discount > 0 ? '' : 'none'
  if (discountEl) discountEl.textContent = `-\u00A3${discount.toFixed(2)}`
  if (discountLabelEl) discountLabelEl.textContent = data.discountLabel || 'Discount'

  // The membership welcome offer gets its OWN row: a Join & Book basket carries
  // a ticket discount as well, and sharing one row silently dropped whichever
  // lost. The server has already subtracted it from data.total; this only
  // labels what is applied.
  const memOffer = data.membershipOffer
  const offerRow = page.querySelector('[data-basket-summary-offer-row]')
  const offerEl = page.querySelector('[data-basket-summary-offer]')
  const offerLabelEl = page.querySelector('[data-basket-summary-offer-label]')
  const offerDiscount = memOffer ? (memOffer.discount || 0) : 0
  if (offerRow) offerRow.style.display = offerDiscount > 0 ? '' : 'none'
  if (offerEl) offerEl.textContent = `-\u00A3${offerDiscount.toFixed(2)}`
  if (offerLabelEl && memOffer) offerLabelEl.textContent = `First month ${memOffer.percent}% off`

  // Donation (set on the checkout page) — shown so the total still adds up
  // when the buyer navigates back here with one applied
  const donationRow = page.querySelector('[data-basket-summary-donation-row]')
  const donationEl = page.querySelector('[data-basket-summary-donation]')
  const donation = data.totalDonation || 0
  if (donationRow) donationRow.style.display = donation > 0 ? '' : 'none'
  if (donationEl) donationEl.textContent = `\u00A3${donation.toFixed(2)}`

  if (totalEl) {
    const t = data.total || 0
    totalEl.textContent = `\u00A3${t.toFixed(2)}`
  }

  // Promo code state — show applied code or input
  const promoForm = page.querySelector('[data-promo-form]')
  const promoApplied = page.querySelector('[data-promo-applied]')
  const promoCode = data.promoCode || null

  if (promoCode) {
    if (promoForm) promoForm.style.display = 'none'
    if (promoApplied) {
      promoApplied.style.display = ''
      const codeEl = promoApplied.querySelector('[data-promo-code]')
      if (codeEl) codeEl.textContent = promoCode
    }
  } else {
    if (promoForm) promoForm.style.display = ''
    if (promoApplied) promoApplied.style.display = 'none'
    // Clear the input
    const input = page.querySelector('[data-promo-input]')
    if (input) input.value = ''
  }

  renderFreeTickets(page, data)
}

/**
 * Render the "Use my free ticket" block. One row per entry in
 * availableFreeTickets; if appliedFreeTicketPromotionId is set, the
 * matching row shows the "applied" state with a remove button.
 */
// #204: the "Apply to" line picker for an applied free ticket. By default it
// only renders when there's a real choice (2+ eligible lines); the Join &
// Book box passes minLines:1 + includeSave so "Save for later" is always an
// option in the dropdown (and, with saveSelected, the box persists in the
// saved state so the buyer can change their mind).
function buildFreeTicketPicker (data, promotionId, opts = {}) {
  const eligibleLines = Array.isArray(data.freeTicketEligibleLines) ? data.freeTicketEligibleLines : []
  if (eligibleLines.length < (opts.minLines || 2)) return ''
  const targetSd = Number(data.freeTicketTargetShowDateId || 0)
  const targetTt = Number(data.freeTicketTargetTicketTypeId || 0)
  // Abbreviate long show/ticket-type labels so the closed select stays
  // inside the summary box (full text still available via title).
  const abbreviate = (s, n) => {
    s = String(s || '')
    return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s
  }
  const lineOpts = eligibleLines.map(l => {
    const selected = (!opts.saveSelected && Number(l.showDateId) === targetSd && Number(l.ticketTypeId) === targetTt) ? ' selected' : ''
    const shortLabel = abbreviate(l.label, 34)
    const optLabel = l.showDate ? `${shortLabel} · ${l.showDate}` : shortLabel
    const fullLabel = l.showDate ? `${l.label} · ${l.showDate}` : l.label
    return `<option value="${escapeHtml(l.showDateId)}:${escapeHtml(l.ticketTypeId)}"${selected} title="${escapeHtml(fullLabel)}">${escapeHtml(optLabel)}</option>`
  }).join('')
  const saveOpt = opts.includeSave
    ? `<option value="save"${opts.saveSelected ? ' selected' : ''}>Save for later</option>`
    : ''
  return `
    <label class="basket-summary__free-ticket-picker">
      <span class="basket-summary__free-ticket-picker-label">Apply to</span>
      <select class="js-free-ticket-target" data-promotion-id="${escapeHtml(promotionId)}">${lineOpts}${saveOpt}</select>
    </label>`
}

function renderFreeTickets (page, data) {
  const block = page.querySelector('[data-free-tickets-block]')
  if (!block) return

  const available = Array.isArray(data.availableFreeTickets) ? data.availableFreeTickets : []
  const appliedId = data.appliedFreeTicketPromotionId ? Number(data.appliedFreeTicketPromotionId) : null

  // Join & Book: the membership free ticket is auto-applied (no manual
  // panel, no remove — it's part of the membership). The customer chooses
  // WHICH ticket is free via the picker, whose dropdown also carries "Save
  // for later" — selecting it declines the auto-apply but the box STAYS on
  // screen in the saved state, so they can change their mind and pick a
  // show again (guests have no other way back to it).
  const savedId = data.autoFreeTicketSaved ? Number(data.autoFreeTicketSavedPromotionId || 0) : 0
  let jnbBox = ''
  if (data.autoFreeTicketApplied && appliedId) {
    const picker = buildFreeTicketPicker(data, appliedId, { includeSave: true, minLines: 1 })
    if (picker) {
      jnbBox = `
        <div class="basket-summary__free-ticket basket-summary__free-ticket--applied">
          <div class="basket-summary__free-ticket-info">
            <strong>${escapeHtml(data.appliedFreeTicketName || 'Free ticket')}</strong> included with your membership
          </div>
          ${picker}
        </div>
      `
    }
  } else if (savedId) {
    const picker = buildFreeTicketPicker(data, savedId, { includeSave: true, saveSelected: true, minLines: 1 })
    if (picker) {
      jnbBox = `
        <div class="basket-summary__free-ticket basket-summary__free-ticket--applied">
          <div class="basket-summary__free-ticket-info">
            <strong>${escapeHtml(data.autoFreeTicketSavedName || 'Free ticket')}</strong> saved for later. Pick a show to use it now.
          </div>
          ${picker}
        </div>
      `
    }
  }

  if (!available.length) {
    block.hidden = !jnbBox
    block.innerHTML = jnbBox
    return
  }

  block.hidden = false
  block.innerHTML = jnbBox + available.map(ft => {
    const isApplied = appliedId === Number(ft.promotionId)
    const ineligible = !isApplied && !ft.hasEligibleLine
    const savings = ft.potentialSavings && ft.potentialSavings > 0
      ? `(saves &pound;${Number(ft.potentialSavings).toFixed(2)})`
      : ''
    if (isApplied) {
      // #204: let the customer choose which show the free ticket applies to,
      // so it doesn't silently sit on the most-expensive one.
      const picker = buildFreeTicketPicker(data, ft.promotionId)
      return `
        <div class="basket-summary__free-ticket basket-summary__free-ticket--applied">
          <div class="basket-summary__free-ticket-info">
            <strong>${escapeHtml(ft.name)}</strong> applied
            <span class="basket-summary__free-ticket-saving">${savings}</span>
          </div>
          <button type="button" class="basket-summary__free-ticket-remove js-remove-free-ticket" data-promotion-id="${escapeHtml(ft.promotionId)}" title="Remove">&times;</button>
          ${picker}
        </div>
      `
    }
    const disabled = ineligible ? 'disabled' : ''
    const subtext = ineligible
      ? 'Add a ticket to a participating show to use this.'
      : `Use it on this basket ${savings}`
    return `
      <div class="basket-summary__free-ticket">
        <div class="basket-summary__free-ticket-info">
          <strong>${escapeHtml(ft.name)}</strong>
          <span class="basket-summary__free-ticket-sub">${subtext}</span>
        </div>
        <button type="button" class="basket-summary__free-ticket-btn js-apply-free-ticket" data-promotion-id="${escapeHtml(ft.promotionId)}" ${disabled}>Use it</button>
      </div>
    `
  }).join('')
}

// ─── Qty stepper (+ / -) ────────────────────────────────────────
// "+" holds one more seat for the same performance + ticket type (the server
// enforces capacity and type rules and errors cleanly); "-" releases one of
// the line's held seats. Both re-render from the server state, so discounts,
// bundles and the free-ticket target all recompute.

// NOTE: the steppers deliberately do NOT clearCrossSellCache(). The cache is
// keyed by the basket's showDate set, which a qty change never alters — and
// when a - removes a line's last seat the key changes, so the stale entry
// misses naturally. Clearing it made every click re-run the expensive
// cross-sell scan and feel sluggish.

// Instant feedback: bump the visible count (and freeze the line's stepper)
// before the round-trip; the full re-render reconciles to server truth.
function nudgeQtyDisplay (btn, delta) {
  const holder = btn.closest('.basket-item__qty--stepper')
  if (!holder) return
  const countEl = holder.querySelector('.basket-item__qty-count')
  if (countEl) {
    const n = parseInt(countEl.textContent, 10)
    if (!isNaN(n)) countEl.textContent = String(Math.max(0, n + delta))
  }
  holder.querySelectorAll('.basket-item__qty-btn').forEach(b => { b.disabled = true })
}

async function handleQtyInc (btn, page) {
  const [showDateId, ticketTypeId] = String(btn.dataset.qtyInc || '').split(':').map(Number)
  if (!showDateId || !ticketTypeId || btn.disabled) return
  nudgeQtyDisplay(btn, 1)
  try {
    const res = await fetch('/api/basket/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Security-Token': csrfToken()
      },
      body: JSON.stringify({ instanceId: showDateId, planId: '', ticketTypeId, qty: 1 })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      showToast(data.error || 'Could not add another ticket', 'error')
      await fetchAndRenderBasket(page)
      return
    }
    await fetchAndRenderBasket(page)
  } catch (err) {
    console.error('Qty increase error:', err)
    showToast('Something went wrong', 'error')
    await fetchAndRenderBasket(page)
  }
}

async function handleQtyDec (btn, page) {
  const ticketId = btn.dataset.qtyDec
  if (!ticketId || btn.disabled) return
  nudgeQtyDisplay(btn, -1)
  try {
    const res = await fetch(`/api/basket/remove/${ticketId}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
    })
    if (!res.ok) {
      showToast('Could not remove ticket', 'error')
      await fetchAndRenderBasket(page)
      return
    }
    await fetchAndRenderBasket(page)
  } catch (err) {
    console.error('Qty decrease error:', err)
    showToast('Something went wrong', 'error')
    await fetchAndRenderBasket(page)
  }
}

// ─── Optimistic remove (#3) ─────────────────────────────────────

async function handleRemoveTicket (btn, page) {
  const ticketId = btn.dataset.removeTicket
  if (!ticketId || btn.disabled) return

  btn.disabled = true

  // Optimistically hide the basket line
  const lineEl = btn.closest('[data-basket-line]')
  if (lineEl) lineEl.style.opacity = '0.3'

  try {
    // Remove ticket via our server-side basket
    const res = await fetch(`/api/basket/remove/${ticketId}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
    })

    if (!res.ok) {
      // Restore the line on failure
      if (lineEl) lineEl.style.opacity = ''
      showToast('Could not remove ticket', 'error')
      btn.disabled = false
      return
    }

    // Invalidate cross-sell cache since basket changed
    clearCrossSellCache()

    // Re-fetch and re-render the whole basket
    await fetchAndRenderBasket(page)

    showToast('Ticket removed', 'success')
  } catch (err) {
    console.error('Remove ticket error:', err)
    if (lineEl) lineEl.style.opacity = ''
    showToast('Something went wrong', 'error')
    btn.disabled = false
  }
}

async function handleAddCompanion (btn, page) {
  const showDateId = btn.dataset.addCompanion
  if (!showDateId || btn.disabled) return

  btn.disabled = true
  const original = btn.textContent
  btn.textContent = 'Adding…'

  try {
    const res = await fetch('/api/basket/companion', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Security-Token': csrfToken()
      },
      body: JSON.stringify({ showDateId: Number(showDateId) })
    })
    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      showToast(data.error || 'Could not add carer ticket', 'error')
      btn.disabled = false
      btn.textContent = original
      return
    }

    // Basket changed — invalidate cross-sell cache and re-render.
    clearCrossSellCache()
    await fetchAndRenderBasket(page)
    showToast('Free carer ticket added', 'success')
  } catch (err) {
    console.error('Add companion error:', err)
    showToast('Something went wrong', 'error')
    btn.disabled = false
    btn.textContent = original
  }
}

async function handleRemoveAll (btn, page) {
  let ticketIds
  try {
    ticketIds = JSON.parse(btn.dataset.removeAll)
  } catch { return }

  if (!ticketIds || !ticketIds.length || btn.disabled) return

  btn.disabled = true
  btn.textContent = 'Removing\u2026'

  // Optimistically hide the basket line
  const lineEl = btn.closest('[data-basket-line]')
  if (lineEl) lineEl.style.opacity = '0.3'

  try {
    // Remove all tickets in parallel
    const results = await Promise.all(
      ticketIds.map(id => fetch(`/api/basket/remove/${id}`, {
        method: 'DELETE',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
      }).catch(() => ({ ok: false })))
    )
    const allOk = results.every(r => r.ok)

    if (!allOk) {
      if (lineEl) lineEl.style.opacity = ''
      showToast('Could not remove tickets', 'error')
      btn.disabled = false
      return
    }

    // Invalidate cross-sell cache since basket changed
    clearCrossSellCache()

    // Re-fetch and re-render
    await fetchAndRenderBasket(page)

    showToast(`${ticketIds.length} tickets removed`, 'success')
  } catch (err) {
    console.error('Remove all error:', err)
    if (lineEl) lineEl.style.opacity = ''
    showToast('Something went wrong', 'error')
    btn.disabled = false
  }
}

// ─── Remove membership ──────────────────────────────────────────

async function handleRemoveMembership (btn, page) {
  const subscriptionId = btn.dataset.removeMembership
  if (!subscriptionId || btn.disabled) return

  btn.disabled = true

  const lineEl = btn.closest('[data-basket-membership]')
  if (lineEl) lineEl.style.opacity = '0.3'

  try {
    const res = await fetch(`/api/basket/remove-membership/${subscriptionId}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
    })

    if (!res.ok) {
      if (lineEl) lineEl.style.opacity = ''
      showToast('Could not remove membership', 'error')
      btn.disabled = false
      return
    }

    clearCrossSellCache()
    await fetchAndRenderBasket(page)
    showToast('Membership removed', 'success')
  } catch (err) {
    console.error('Remove membership error:', err)
    if (lineEl) lineEl.style.opacity = ''
    showToast('Something went wrong', 'error')
    btn.disabled = false
  }
}

// ─── Remove gift voucher ────────────────────────────────────────

async function handleRemoveGiftVoucher (btn, page) {
  const giftVoucherId = btn.dataset.removeGiftVoucher
  if (!giftVoucherId || btn.disabled) return

  btn.disabled = true

  const lineEl = btn.closest('[data-basket-gift-voucher]')
  if (lineEl) lineEl.style.opacity = '0.3'

  try {
    const res = await fetch(`/api/basket/remove-gift-voucher/${giftVoucherId}`, {
      method: 'DELETE',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() }
    })

    if (!res.ok) {
      if (lineEl) lineEl.style.opacity = ''
      showToast('Could not remove gift voucher', 'error')
      btn.disabled = false
      return
    }

    clearCrossSellCache()
    await fetchAndRenderBasket(page)
    showToast('Gift voucher removed', 'success')
  } catch (err) {
    console.error('Remove gift voucher error:', err)
    if (lineEl) lineEl.style.opacity = ''
    showToast('Something went wrong', 'error')
    btn.disabled = false
  }
}

// ═════════════════════════════════════════════════════════════════
// PERSISTENT BASKET BAR — bottom of every page (except basket/checkout)
// ═════════════════════════════════════════════════════════════════

let barEl = null

function isCompactBasketContext () {
  // Dashboard on mobile: swap the full basket bar for a compact right-side pill.
  return window.innerWidth <= 900 && !!document.querySelector('.dash')
}

function setupBasketBar () {
  // Don't show on basket page or any checkout page
  if (document.querySelector('[data-basket-page]')) return
  if (window.location.pathname.includes('/purchase/checkout')) return
  if (window.location.pathname.includes('/purchase/custom-checkout')) return

  // Bar is created lazily (in showBasketBar) so an empty basket leaves no DOM
  // node behind. Fetching only happens when cached count > 0.
  const { count } = loadBasketCount()
  if (count > 0) {
    refreshBasketBar()
  }
}

function ensureBasketBar () {
  if (barEl) return barEl
  barEl = document.createElement('div')
  if (isCompactBasketContext()) {
    barEl.className = 'basket-bar basket-bar--compact'
    barEl.innerHTML = `
      <a href="/purchase/basket" class="basket-bar__pill" aria-label="View basket">
        <svg viewBox="0 0 24 24"><path d="M3 3h2l2.4 12.5a2 2 0 0 0 2 1.5h7.7a2 2 0 0 0 2-1.6L21 8H6"/><circle cx="9" cy="21" r="1"/><circle cx="18" cy="21" r="1"/></svg>
        <span class="basket-bar__count"></span>
        <span class="basket-bar__dot">&middot;</span>
        <span class="basket-bar__total"></span>
      </a>
    `
  } else {
    barEl.className = 'basket-bar'
    barEl.innerHTML = `
      <div class="basket-bar__inner">
        <div class="basket-bar__thumbs"></div>
        <div class="basket-bar__info">
          <span class="basket-bar__count"></span>
          <span class="basket-bar__dot">&middot;</span>
          <span class="basket-bar__total"></span>
        </div>
        <div class="basket-bar__actions">
          <a href="/purchase/basket" class="basket-bar__view">View Basket</a>
          <a href="/purchase/checkout" class="basket-bar__checkout">Checkout</a>
        </div>
      </div>
    `
  }
  document.body.appendChild(barEl)
  return barEl
}

async function refreshBasketBar () {
  try {
    const res = await fetch('/api/basket/get')
    if (!res.ok) return

    const data = await res.json()
    if (data.expired) { hideBasketBar(); return }

    const hasItems = (data.grouped && data.grouped.length > 0) ||
                     (data.memberships && data.memberships.length > 0) ||
                     (data.giftVouchers && data.giftVouchers.length > 0)
    if (!hasItems) {
      hideBasketBar()
      return
    }

    ensureBasketBar()
    renderBasketBar(data)
    showBasketBar()

    // Skip cross-sell strip in compact mode — the pill has no room for it.
    if (!barEl.classList.contains('basket-bar--compact')) {
      const instanceIds = extractInstanceIds(data.grouped)
      if (instanceIds.length > 0) {
        fetchCrossSells(instanceIds).then(cs => renderCrossSellStrip(cs))
      }
    }
  } catch (err) {
    console.error('Basket bar error:', err)
  }
}

function renderBasketBar (data) {
  if (!barEl) return

  // Thumbs don't exist in compact mode, skip them.
  const thumbsEl = barEl.querySelector('.basket-bar__thumbs')
  if (thumbsEl) {
    const seen = new Set()
    const unique = data.grouped.filter(line => {
      if (seen.has(line.showName)) return false
      seen.add(line.showName)
      return true
    }).slice(0, 4)

    thumbsEl.innerHTML = unique.map(line =>
      line.showImage
        ? `<img class="basket-bar__thumb" src="${escapeHtml(line.showImage)}" alt="${escapeHtml(line.showName)}">`
        : `<div class="basket-bar__thumb basket-bar__thumb--placeholder">
             <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="18" rx="2"/></svg>
           </div>`
    ).join('')
  }

  // Count + total
  const n = data.count || 0
  const t = data.total || 0
  barEl.querySelector('.basket-bar__count').textContent = n === 1 ? '1 ticket' : `${n} tickets`
  barEl.querySelector('.basket-bar__total').textContent = `\u00A3${t.toFixed(2)}`
}

function showBasketBar () {
  if (!barEl) return
  barEl.classList.add('basket-bar--visible')
  document.body.classList.add('has-basket-bar')
}

function hideBasketBar () {
  document.body.classList.remove('has-basket-bar')
  if (!barEl) return
  barEl.classList.remove('basket-bar--visible')
  // Remove from DOM after the slide-down transition so an empty bar never
  // lingers in the layout.
  setTimeout(function () {
    if (barEl && !barEl.classList.contains('basket-bar--visible')) {
      barEl.remove()
      barEl = null
    }
  }, 400)
}

// ═════════════════════════════════════════════════════════════════
// CROSS-SELL — multibuy discount suggestions
// ═════════════════════════════════════════════════════════════════

/** Extract unique Spektrix instance IDs from enriched grouped data. */
function extractInstanceIds (grouped) {
  const ids = new Set()
  for (const line of grouped) {
    // key format: "instanceId|typeId"
    const instId = line.key.split('|')[0]
    if (instId && instId !== 'unknown') ids.add(instId)
  }
  return [...ids]
}

// ─── Cross-sell sessionStorage cache (#4) ────────────────────────

const CROSS_SELL_CACHE_KEY = 'fa_cross_sells'

function getCrossSellCache (instanceIds) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CROSS_SELL_CACHE_KEY) || 'null')
    if (!cached) return null
    const key = [...instanceIds].sort().join(',')
    if (cached.key === key) return cached.data
  } catch {}
  return null
}

function setCrossSellCache (instanceIds, data) {
  try {
    const key = [...instanceIds].sort().join(',')
    sessionStorage.setItem(CROSS_SELL_CACHE_KEY, JSON.stringify({ key, data }))
  } catch {}
}

function clearCrossSellCache () {
  try { sessionStorage.removeItem(CROSS_SELL_CACHE_KEY) } catch {}
}

/** Fetch cross-sell suggestions from our API (with cache). */
async function fetchCrossSells (instanceIds) {
  // #4 — Check cache first
  const cached = getCrossSellCache(instanceIds)
  if (cached) return cached

  try {
    const res = await fetch('/api/basket/cross-sells', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() },
      body: JSON.stringify({ instanceIds })
    })
    if (!res.ok) return []
    const data = await res.json()
    const crossSells = data.crossSells || []
    // Defensive: any tier whose discountLabel is just the generic "you
    // may also like" prompt (sometimes admin-set on an offer) shouldn't
    // be treated as a discount call-out — empty string flips the
    // renderers to the soft "you might also like X" sentence pattern.
    crossSells.forEach(cs => {
      if (typeof cs.discountLabel === 'string'
          && /^you (may|might) also like/i.test(cs.discountLabel.trim())) {
        cs.discountLabel = ''
      }
    })
    setCrossSellCache(instanceIds, crossSells)
    return crossSells
  } catch (err) {
    console.error('Cross-sell fetch error:', err)
    return []
  }
}

/** Build a context suffix for a cross-sell suggestion. */
function crossSellSuffix (cs) {
  if (cs.priority === 'same-night') return ' on the same night'
  if (cs.showDateTime) return ` (${cs.showDateTime})`
  return ''
}

/** Sentence for a discount cross-sell (titleHtml is pre-escaped markup).
    The multibuy discounts BOTH shows' tickets, not just the added one, so an
    amount label ("25% off" / "£5 off" from CrossSellService::labelFor) reads
    "…and get 25% off both shows!"; a promotion-name fallback label keeps the
    generic "for <label>!" shape. */
function crossSellDealSentence (titleHtml, suffix, label) {
  return /\boff$/i.test(label.trim())
    ? `Add ${titleHtml}${escapeHtml(suffix)} and get <strong>${escapeHtml(label)} both shows</strong>!`
    : `Add ${titleHtml}${escapeHtml(suffix)} for ${escapeHtml(label)}!`
}

/** Render cross-sell strip above the basket bar. */
function renderCrossSellStrip (crossSells) {
  if (!barEl) return

  // Remove existing cross-sell strip
  const existing = barEl.querySelector('.basket-bar__cross-sell')
  if (existing) existing.remove()

  if (!crossSells || crossSells.length === 0) return

  const cs = crossSells[0] // Show top suggestion only
  const suffix = crossSellSuffix(cs)
  // Tier 1 (offer pair) gets a discount-call-out heading + "Add X for <discount>";
  // tier 2 / 3 (boosted, same-category) get a softer "You might also like X".
  const eyebrow = cs.discountLabel ? 'SPECIAL OFFER' : 'YOU MIGHT ALSO LIKE'
  const sentence = cs.discountLabel
    ? crossSellDealSentence(`<a href="${escapeHtml(cs.showLink)}" class="basket-bar__cross-sell-link"><strong>${escapeHtml(cs.showTitle)}</strong></a>`, suffix, cs.discountLabel)
    : `<a href="${escapeHtml(cs.showLink)}" class="basket-bar__cross-sell-link"><strong>${escapeHtml(cs.showTitle)}</strong></a>${escapeHtml(suffix)}`
  const strip = document.createElement('div')
  strip.className = 'basket-bar__cross-sell'
  strip.innerHTML = `
    <div class="basket-bar__cross-sell-inner">
      ${cs.showImage ? `<img class="basket-bar__cross-sell-thumb" src="${escapeHtml(cs.showImage)}" alt="">` : ''}
      <div class="basket-bar__cross-sell-text">
        <span class="basket-bar__cross-sell-label">${eyebrow}</span>
        ${sentence}
        <button class="basket-bar__cross-sell-add" data-cross-sell-date="${escapeHtml(cs.showDateId)}">Add to Basket</button>
      </div>
    </div>
  `

  barEl.insertBefore(strip, barEl.firstChild)

  // Wire up Add button
  strip.querySelector('[data-cross-sell-date]').addEventListener('click', (e) => {
    handleCrossSellAdd(e.target, cs.showDateId)
  })
}

/** Render the show cross-sell row inside the basket page's offers box. */
function renderCrossSellRow (page, crossSells) {
  const rows = page.querySelector('[data-basket-offers-rows]')
  const existing = page.querySelector('.basket-offer--show')
  if (existing) existing.remove()

  if (rows && crossSells && crossSells.length > 0) {
    const cs = crossSells[0] // Show top suggestion only
    // Title ("Add X and save 25%") + description ("On the same night as your
    // tickets — 25% off both shows."). An amount label from
    // CrossSellService::labelFor ("25% off" / "£5 off") becomes "save 25%" /
    // "save £5"; a promotion-name fallback label rides in the description.
    const label = (cs.discountLabel || '').trim()
    const context = cs.priority === 'same-night'
      ? 'On the same night as your tickets'
      : (cs.showDateTime || '')
    const saveMatch = label.match(/^(.+?)\s+off$/i)
    let title, desc
    if (!label) {
      title = `You might also like ${cs.showTitle}`
      desc = context ? `${context}.` : ''
    } else {
      title = saveMatch ? `Add ${cs.showTitle} and save ${saveMatch[1]}` : `Add ${cs.showTitle}`
      const dealBit = saveMatch ? `${label} both shows` : label
      desc = context ? `${context}. ${dealBit}.` : `${dealBit}.`
    }

    const row = document.createElement('div')
    row.className = 'basket-offer basket-offer--show'
    row.innerHTML = `
      ${cs.showImage
        ? `<img class="basket-offer__image" src="${escapeHtml(cs.showImage)}" alt="">`
        : `<div class="basket-offer__image basket-offer__image--placeholder"></div>`}
      <div class="basket-offer__content">
        <p class="basket-offer__title">${escapeHtml(title)}</p>
        ${desc ? `<span class="basket-offer__detail">${escapeHtml(desc)}</span>` : ''}
        ${cs.fromPrice ? `<span class="basket-offer__price">${escapeHtml(cs.fromPrice)}</span>` : ''}
      </div>
      <div class="basket-offer__actions">
        <a href="${escapeHtml(cs.showLink)}" class="basket-offer__link">View Show</a>
        <button class="basket-offer__btn" data-cross-sell-date="${escapeHtml(cs.showDateId)}">Add to Basket</button>
      </div>
    `
    // Show suggestion sits above the membership row
    rows.insertBefore(row, rows.firstChild)

    row.querySelector('[data-cross-sell-date]').addEventListener('click', (e) => {
      handleCrossSellAdd(e.target, cs.showDateId)
    })
  }

  updateOffersBoxVisibility(page)
}

/** The offers box shows only while it holds a visible row. */
function updateOffersBoxVisibility (page) {
  const box = page.querySelector('[data-basket-offers]')
  if (!box) return
  box.hidden = !box.querySelector('.basket-offer:not([hidden])')
}

/** Handle adding a cross-sell show to the basket. */
async function handleCrossSellAdd (btn, showDateId) {
  if (btn.disabled) return
  btn.disabled = true
  const originalText = btn.textContent
  btn.textContent = 'Adding\u2026'

  try {
    const optRes = await fetch(`/api/basket/options/${showDateId}`)
    const optData = await optRes.json()

    if (!optRes.ok || !optData.ticketTypes || optData.ticketTypes.length === 0) {
      showToast(optData.error || 'Could not add show', 'error')
      return
    }

    const { instanceId, planId, ticketTypes, status } = optData

    // #10 — Check availability for cross-sell too
    if (status && status.available !== undefined && status.available <= 0) {
      showToast('Sorry, this show has just sold out', 'error')
      return
    }

    // Add 1 ticket of the cheapest type
    const typeId = ticketTypes[0].id

    // Add via server-side basket
    const addRes = await fetch('/api/basket/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': csrfToken() },
      body: JSON.stringify({ instanceId, planId, ticketTypeId: typeId, qty: 1 })
    })

    if (!addRes.ok) {
      const err = await addRes.json().catch(() => ({}))
      showToast(err.error || 'Could not add to basket', 'error')
      return
    }

    const { count: prevCount } = loadBasketCount()
    const newCount = prevCount + 1
    saveBasketCount(newCount)
    updateHeaderCount(newCount)

    // Invalidate cross-sell cache
    clearCrossSellCache()

    showToast('Show added to basket!', 'success')

    // Refresh — if on basket page, re-render everything; otherwise refresh bar
    const page = document.querySelector('[data-basket-page]')
    if (page) {
      fetchAndRenderBasket(page)
    } else {
      refreshBasketBar()
    }
  } catch (err) {
    console.error('Cross-sell add error:', err)
    showToast('Something went wrong', 'error')
  } finally {
    btn.disabled = false
    btn.textContent = originalText
  }
}

// ─── Utility ─────────────────────────────────────────────────────

// Safe in both HTML text and attribute contexts (encodes the full 5-char set).
// The previous `div.textContent` version didn't encode quotes, so attribute
// values with `"` would break the DOM.
function escapeHtml (str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ─── Basket hold countdown ───────────────────────────────────────

let basketTimerInterval = null

function startBasketTimer (page, expiresAt) {
  const timerEl = page.querySelector('[data-basket-timer]')
  if (!timerEl) return

  if (basketTimerInterval) clearInterval(basketTimerInterval)

  const pad = n => (n < 10 ? '0' + n : '' + n)
  // Server-resolved ShowListingPage link (data-shows-url on the timer el) —
  // a hardcoded /shows 404s on prod, where the listing lives elsewhere.
  const showsUrl = timerEl.dataset.showsUrl || '/shows'

  function render () {
    const remaining = expiresAt - Math.floor(Date.now() / 1000)
    if (remaining <= 0) {
      timerEl.innerHTML = 'Your tickets have been released. <a href="' + escapeHtml(showsUrl) + '">Browse shows</a>'
      timerEl.classList.remove('basket-timer--warning')
      timerEl.classList.add('basket-timer--expired')
      timerEl.style.display = ''
      clearInterval(basketTimerInterval)
      return
    }
    const mins = Math.floor(remaining / 60)
    const secs = remaining % 60
    timerEl.innerHTML = 'Tickets held for <strong>' + pad(mins) + ':' + pad(secs) + '</strong>'
    timerEl.classList.toggle('basket-timer--warning', remaining <= 120)
    timerEl.style.display = ''
  }

  render()
  basketTimerInterval = setInterval(render, 1000)
}
